import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { ImagePlus, Trash2 } from "lucide-react";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";

const EMPTY_NOTE = {
  title: "",
  content: "<p></p>",
  visibility: "PRIVATE",
};

const NOTES_CACHE_TTL_MS = 10000;
let notesListCache = {
  list: [],
  at: 0,
};

export default function MonteurNotesPage() {
  const { user } = useUser();
  const fileInputRef = useRef(null);
  const notesRef = useRef([]);
  const activeIdRef = useRef("");
  const localPreviewUrlsRef = useRef({});
  const resolvedImageUrlsRef = useRef({});
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [form, setForm] = useState(EMPTY_NOTE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [editorFocused, setEditorFocused] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [removedPhotos, setRemovedPhotos] = useState([]);
  const [resolvedImageUrls, setResolvedImageUrls] = useState({});

  const currentUserId = getCurrentUserId(user);

  const activeNote = useMemo(
    () => notes.find((note) => String(note?._id || "") === String(activeId || "")) || null,
    [notes, activeId]
  );

  const canEditActive = useMemo(() => {
    if (!activeNote) return true;
    return String(activeNote?.userId?._id || activeNote?.userId || "") === String(currentUserId || "");
  }, [activeNote, currentUserId]);

  const imageSources = useMemo(() => extractImageSources(form.content), [form.content]);
  const displayContent = useMemo(
    () => replaceImageSources(form.content, resolvedImageUrls),
    [form.content, resolvedImageUrls]
  );

  function clearLocalPreviews() {
    Object.values(localPreviewUrlsRef.current).forEach((url) => {
      window.URL.revokeObjectURL(url);
    });
    localPreviewUrlsRef.current = {};
  }

  function registerResolvedImageUrl(originalSrc, resolvedSrc) {
    const nextMap = {
      ...resolvedImageUrlsRef.current,
      [String(originalSrc || "").trim()]: String(resolvedSrc || "").trim(),
    };
    resolvedImageUrlsRef.current = nextMap;
    setResolvedImageUrls(nextMap);
  }

  function upsertNoteInState(note) {
    if (!note?._id) return;
    const normalizedId = String(note._id);
    setNotes((prev) => {
      const withoutCurrent = prev.filter((item) => String(item?._id) !== normalizedId);
      return [note, ...withoutCurrent];
    });
  }

  const editor = useEditor({
    extensions: [StarterKit, Image.configure({ allowBase64: true })],
    content: displayContent,
    onFocus() {
      setEditorFocused(true);
    },
    onBlur() {
      setEditorFocused(false);
    },
    onUpdate({ editor: currentEditor }) {
      setForm((prev) => ({
        ...prev,
        content: restoreImageSources(currentEditor.getHTML(), resolvedImageUrlsRef.current),
      }));
    },
  });

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  useEffect(() => {
    resolvedImageUrlsRef.current = resolvedImageUrls;
  }, [resolvedImageUrls]);

  useEffect(() => {
    return () => {
      Object.values(localPreviewUrlsRef.current).forEach((url) => {
        window.URL.revokeObjectURL(url);
      });
      localPreviewUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!editor) return;
    if (editorFocused) return;
    const html = String(displayContent || "<p></p>");
    if (editor.getHTML() !== html) {
      editor.commands.setContent(html, false);
    }
  }, [editor, displayContent, editorFocused]);

  useEffect(() => {
    let active = true;
    const objectUrls = [];

    async function resolveSecureImages() {
      const secureSources = imageSources.filter((src) => isSecureNoteImageUrl(src));
      if (secureSources.length === 0) {
        if (active) setResolvedImageUrls({});
        return;
      }

      const entries = await Promise.all(
        secureSources.map(async (src) => {
          try {
            const res = await api.get(toApiRelativeUrl(src), { responseType: "blob" });
            const stalePreviewUrl = localPreviewUrlsRef.current[src];
            if (stalePreviewUrl) {
              window.URL.revokeObjectURL(stalePreviewUrl);
              delete localPreviewUrlsRef.current[src];
            }
            const objectUrl = window.URL.createObjectURL(res.data);
            objectUrls.push(objectUrl);
            return [src, objectUrl];
          } catch {
            return [src, localPreviewUrlsRef.current[src] || src];
          }
        })
      );

      if (!active) return;
      setResolvedImageUrls(Object.fromEntries(entries));
    }

    resolveSecureImages();

    return () => {
      active = false;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [imageSources]);

  const handleSelect = useCallback(async (id, currentList = null, options = {}) => {
    const targetId = String(id || "");
    if (!targetId) return;
    const forceFetch = options?.forceFetch === true;

    setActiveId(targetId);
    setSuccess("");
    setError("");
    setRemovedPhotos([]);
    clearLocalPreviews();
    setResolvedImageUrls({});

    const sourceList = Array.isArray(currentList) ? currentList : notesRef.current;
    const lightweight = sourceList.find((item) => String(item?._id) === targetId);
    if (lightweight) {
      setForm({
        title: String(lightweight.title || ""),
        content: String(lightweight.content || "<p></p>"),
        visibility: normalizeVisibility(lightweight.visibility),
      });
      const hasInlineContent = Object.prototype.hasOwnProperty.call(
        lightweight || {},
        "content"
      );
      if (hasInlineContent && !forceFetch) {
        return;
      }
    }

    try {
      const res = await api.get(`/notes/${targetId}`);
      const item = res.data || {};
      setForm({
        title: String(item.title || ""),
        content: String(item.content || "<p></p>"),
        visibility: normalizeVisibility(item.visibility),
      });
      setNotes((prev) =>
        prev.map((note) => (String(note?._id) === targetId ? { ...note, ...item } : note))
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Notitie ophalen mislukt");
    }
  }, []);

  const loadNotes = useCallback(async (preferredId = "") => {
    try {
      setLoading(true);
      setError("");
      const ageMs = Date.now() - Number(notesListCache.at || 0);
      if (!preferredId && Array.isArray(notesListCache.list) && notesListCache.list.length > 0 && ageMs >= 0 && ageMs < NOTES_CACHE_TTL_MS) {
        const cachedList = notesListCache.list;
        setNotes(cachedList);
        const nextId = activeIdRef.current;
        if (nextId && cachedList.some((item) => String(item?._id) === String(nextId))) {
          await handleSelect(nextId, cachedList);
        } else if (cachedList.length > 0) {
          await handleSelect(cachedList[0]._id, cachedList);
        }
        return;
      }
      const res = await api.get("/notes");
      const list = Array.isArray(res.data) ? res.data : [];
      notesListCache = {
        list,
        at: Date.now(),
      };
      setNotes(list);

      const nextId = preferredId || activeIdRef.current;
      if (nextId && list.some((item) => String(item?._id) === String(nextId))) {
        await handleSelect(nextId, list);
        return;
      }

      if (list.length > 0) {
        await handleSelect(list[0]._id, list);
      } else {
        setActiveId("");
        setForm(EMPTY_NOTE);
        setRemovedPhotos([]);
        setResolvedImageUrls({});
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Notities laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [handleSelect]);

  useEffect(() => {
    // Initial load should run once per mount to avoid accidental re-fetch loops.
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleNew() {
    setActiveId("");
    setForm(EMPTY_NOTE);
    setError("");
    setSuccess("");
    setRemovedPhotos([]);
    clearLocalPreviews();
    setResolvedImageUrls({});
    editor?.commands.setContent("<p></p>", false);
  }

  async function handleSave() {
    if (pendingUploadCount > 0) {
      setError("Wacht tot alle afbeeldingen volledig zijn geüpload.");
      return;
    }

    const title = String(form.title || "").trim();
    if (!title) {
      setError("Titel is verplicht");
      return;
    }

    if (activeId && !canEditActive) {
      setError("Je kunt alleen je eigen notities wijzigen.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const persistedContent = restoreImageSources(
        editor?.getHTML() || form.content,
        resolvedImageUrlsRef.current
      );

      const payload = {
        title,
        content: String(persistedContent || "<p></p>"),
        visibility: normalizeVisibility(form.visibility),
        photos: extractImageSources(persistedContent),
      };

      if (activeId && removedPhotos.length > 0) {
        payload.removePhotos = removedPhotos;
      }

      if (!activeId) {
        const res = await api.post("/notes", payload);
        setSuccess("Notitie opgeslagen");
        notesListCache = { list: [], at: 0 };
        setRemovedPhotos([]);
        if (res?.data?._id) {
          setActiveId(String(res.data._id));
          upsertNoteInState(res.data);
        }
        return;
      }

      const res = await api.patch(`/notes/${activeId}`, payload);
      setSuccess("Notitie bijgewerkt");
      notesListCache = { list: [], at: 0 };
      setRemovedPhotos([]);
      if (res?.data?._id || activeId) {
        setActiveId(String(res?.data?._id || activeId));
      }
      if (res?.data) {
        upsertNoteInState(res.data);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Notitie opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!activeId) return;
    if (!canEditActive) {
      setError("Je kunt alleen je eigen notities verwijderen.");
      return;
    }
    const ok = window.confirm("Notitie verwijderen?");
    if (!ok) return;

    try {
      setDeleting(true);
      setError("");
      setSuccess("");
      await api.delete(`/notes/${activeId}`);
      setSuccess("Notitie verwijderd");
      notesListCache = { list: [], at: 0 };
      await loadNotes("");
    } catch (err) {
      setError(err?.response?.data?.message || "Notitie verwijderen mislukt");
    } finally {
      setDeleting(false);
    }
  }

  async function insertImageFromFile(file) {
    if (!file || !editor || !canEditActive) return;
    setPendingUploadCount((prev) => prev + 1);
    try {
      setUploadingImage(true);
      setError("");
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/notes/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const src = String(res?.data?.url || "").trim();
      if (!src) throw new Error("Geen afbeeldings-url ontvangen");
      const previewUrl = window.URL.createObjectURL(file);
      const stalePreviewUrl = localPreviewUrlsRef.current[src];
      if (stalePreviewUrl) {
        window.URL.revokeObjectURL(stalePreviewUrl);
      }
      localPreviewUrlsRef.current[src] = previewUrl;
      registerResolvedImageUrl(src, previewUrl);
      editor
        .chain()
        .focus("end")
        .insertContent(`<img src="${escapeHtmlAttribute(previewUrl)}" alt="" /><p></p>`)
        .focus("end")
        .run();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Afbeelding upload mislukt");
    } finally {
      setPendingUploadCount((prev) => Math.max(0, prev - 1));
      setUploadingImage(false);
    }
  }

  async function insertImagesFromFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    for (const file of files) {
      // Sequential uploads keep editor state predictable and preserve image order.
      // eslint-disable-next-line no-await-in-loop
      await insertImageFromFile(file);
    }
  }

  function triggerImagePicker() {
    if (!canEditActive) return;
    fileInputRef.current?.click();
  }

  function handleRemoveImage(src) {
    if (!canEditActive || !editor) return;
    const previewUrl = localPreviewUrlsRef.current[src];
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      delete localPreviewUrlsRef.current[src];
    }
    const nextContent = removeImageFromHtml(form.content, src);
    setForm((prev) => ({ ...prev, content: nextContent }));
    editor.commands.setContent(nextContent, false);
    if (activeId) {
      setRemovedPhotos((prev) => Array.from(new Set([...prev, src])));
    }
    setSuccess("");
    setError("");
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Notities</h2>
            <p className="mt-1 text-sm text-gray-600">Snelle technische notities voor jezelf of je team.</p>
          </div>
          <button
            type="button"
            onClick={handleNew}
            className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            Nieuwe notitie
          </button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-white p-3 shadow-sm">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Lijst</h3>
          {loading && <p className="text-sm text-gray-500">Laden...</p>}
          {!loading && notes.length === 0 && <p className="text-sm text-gray-500">Nog geen notities.</p>}
          <div className="space-y-2">
            {notes.map((note) => {
              const isActive = String(note?._id || "") === String(activeId || "");
              return (
                <button
                  key={note._id}
                  type="button"
                  onClick={() => handleSelect(note._id)}
                  className={`w-full rounded border px-3 py-2 text-left ${
                    isActive ? "border-blue-600 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <p className="truncate text-sm font-semibold text-gray-800">{note.title || "-"}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {visibilityLabel(note.visibility)} | {formatDateTime(note.updatedAt)}
                  </p>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="rounded-xl border bg-white p-4 shadow-sm">
          {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
          {success && <p className="mb-2 text-sm text-emerald-700">{success}</p>}
          {pendingUploadCount > 0 && (
            <p className="mb-2 text-sm text-amber-700">
              {pendingUploadCount === 1
                ? "1 afbeelding wordt nog geüpload. Opslaan is tijdelijk geblokkeerd."
                : `${pendingUploadCount} afbeeldingen worden nog geüpload. Opslaan is tijdelijk geblokkeerd.`}
            </p>
          )}
          {!canEditActive && (
            <p className="mb-2 text-xs text-amber-700">Deze teamnotitie is alleen-lezen omdat hij van een andere monteur is.</p>
          )}

          <div className="mb-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
            <input
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Titel"
              disabled={!canEditActive}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
            />
            <select
              value={normalizeVisibility(form.visibility)}
              onChange={(event) => setForm((prev) => ({ ...prev, visibility: event.target.value }))}
              disabled={!canEditActive}
              className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
            >
              <option value="PRIVATE">Private</option>
              <option value="TEAM">Team</option>
            </select>
          </div>

          <div className="mb-2 flex flex-wrap items-center gap-2">
            <ToolbarButton label="Bold" onClick={() => editor?.chain().focus().toggleBold().run()} disabled={!canEditActive} />
            <ToolbarButton label="Italic" onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={!canEditActive} />
            <ToolbarButton
              label="Bullet list"
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              disabled={!canEditActive}
            />
            <ToolbarButton
              label="Numbered list"
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              disabled={!canEditActive}
            />
            <ToolbarButton
              label={uploadingImage ? "Uploading..." : "Image upload"}
              onClick={triggerImagePicker}
              disabled={!canEditActive || pendingUploadCount > 0}
              icon={ImagePlus}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(event) => {
                void insertImagesFromFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </div>

          <div className="note-editor min-h-[260px] rounded border border-gray-300 bg-white px-3 py-2">
            <EditorContent editor={editor} />
          </div>

          {imageSources.length > 0 && (
            <div className="mt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Afbeeldingen</p>
              <div className="flex flex-wrap gap-3">
                {imageSources.map((src) => (
                  <div
                    key={src}
                    className="group relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <img
                      src={resolvedImageUrls[src] || src}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                    {canEditActive && (
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(src)}
                        className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-600 shadow transition hover:bg-white"
                        aria-label="Afbeelding verwijderen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !canEditActive || pendingUploadCount > 0}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Opslaan..." : pendingUploadCount > 0 ? "Upload bezig..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!activeId || deleting || !canEditActive}
              className="rounded border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              {deleting ? "Verwijderen..." : "Delete"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function ToolbarButton({ label, onClick, disabled, icon: Icon = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {label}
    </button>
  );
}

function normalizeVisibility(value) {
  return String(value || "PRIVATE").trim().toUpperCase() === "TEAM" ? "TEAM" : "PRIVATE";
}

function visibilityLabel(value) {
  return normalizeVisibility(value) === "TEAM" ? "Team" : "Private";
}

function getCurrentUserId(user) {
  return user?._id || user?.userId || user?.id || user?.sub || "";
}

function extractImageSources(html = "") {
  if (!html || typeof window === "undefined") return [];
  const doc = new window.DOMParser().parseFromString(String(html), "text/html");
  const srcSet = new Set();
  doc.querySelectorAll("img").forEach((img) => {
    const src = String(img.getAttribute("src") || "").trim();
    if (src) srcSet.add(src);
  });
  return Array.from(srcSet).slice(0, 30);
}

function removeImageFromHtml(html = "", srcToRemove = "") {
  if (typeof window === "undefined") return String(html || "");
  const doc = new window.DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src = String(img.getAttribute("src") || "").trim();
    if (src === String(srcToRemove || "").trim()) {
      img.remove();
    }
  });
  const nextHtml = String(doc.body.innerHTML || "").trim();
  return nextHtml || "<p></p>";
}

function isSecureNoteImageUrl(value = "") {
  const raw = String(value || "").trim();
  return (
    /^\/api\/notes\/images\/[^/]+\/[^/]+$/i.test(raw) ||
    /^https?:\/\/[^/]+\/api\/notes\/images\/[^/]+\/[^/]+$/i.test(raw)
  );
}

function toApiRelativeUrl(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  try {
    if (/^https?:\/\//i.test(raw)) {
      const url = new URL(raw);
      return `${url.pathname}${url.search}`;
    }
  } catch {
    return raw;
  }
  return raw.startsWith("/api/") ? raw.slice(4) : raw;
}

function escapeHtmlAttribute(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function replaceImageSources(html = "", resolvedMap = {}) {
  if (typeof window === "undefined") return String(html || "");
  const doc = new window.DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src = String(img.getAttribute("src") || "").trim();
    const resolved = String(resolvedMap?.[src] || "").trim();
    if (resolved) {
      img.setAttribute("src", resolved);
    }
  });
  return String(doc.body.innerHTML || "").trim() || "<p></p>";
}

function restoreImageSources(html = "", resolvedMap = {}) {
  if (typeof window === "undefined") return String(html || "");
  const reverseMap = new Map(
    Object.entries(resolvedMap || {}).map(([original, resolved]) => [
      String(resolved || ""),
      String(original || ""),
    ])
  );
  const doc = new window.DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src = String(img.getAttribute("src") || "").trim();
    const original = reverseMap.get(src);
    if (original) {
      img.setAttribute("src", original);
    }
  });
  return String(doc.body.innerHTML || "").trim() || "<p></p>";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
